// Company stores (S4 shop, 2026-10-12) — /store/admin/approvals (store admin).
//
// The orders waiting for the admin's OK (GET /portal/store/admin/orders
// ?status=pending_approval): who, what, the total, when. Approve sends the
// total the admin saw (`expectedTotal`); if the price has moved since, the
// server answers 409 { newTotal } and the admin sees the new price and
// approves again. A company-card order is "Approve & pay": the server hands
// back a Stripe Checkout and the admin pays there. "Decline" needs a
// reason (the person is emailed it). An admin can also cancel an order.
//
// UX review (2026-10-12): a second group, "Approved — waiting for you to
// pay" (GET …/admin/orders?status=awaiting_payment, company-card orders
// only), with "Pay now" — an admin who approved and then left Stripe is
// never stranded. Both groups count in the menu's Approvals badge.
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import { StoreCancelOrder } from "@/components/StoreCancelOrder";
import { CARD, INPUT, LINK, Notice, PRIMARY, SECONDARY } from "@/components/StoreShopUi";
import { apiMessage, formatDate } from "@/lib/portal-store";
import {
  approveStoreOrder,
  fetchAdminOrders,
  fetchAdminOrdersToPay,
  itemsSummary,
  money,
  newTotalFrom,
  paymentMethodWords,
  rejectStoreOrder,
  STORE_ADMIN_TO_PAY_KEY,
  STORE_ORDERS_KEY,
  STORE_PENDING_KEY,
  type ApproveResult,
  type StoreOrder,
} from "@/lib/store-shop";
import { usePayNow } from "@/pages/StoreOrdersPage";

export function StoreApprovalsPage() {
  return (
    <PortalLayout>
      <StoreGate admin>
        {({ store }) => (
          <>
            <StoreHeading store={store} title="Approvals" />
            <ApprovalsList />
          </>
        )}
      </StoreGate>
    </PortalLayout>
  );
}

export function usePendingApprovals(enabled = true) {
  return useQuery({
    queryKey: STORE_PENDING_KEY,
    queryFn: () => fetchAdminOrders("pending_approval"),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

/** Approved company-card orders waiting for the admin to pay. */
export function useOrdersToPay(enabled = true) {
  return useQuery({
    queryKey: STORE_ADMIN_TO_PAY_KEY,
    queryFn: fetchAdminOrdersToPay,
    enabled,
    staleTime: 0,
    retry: false,
  });
}

function ApprovalsList() {
  const q = usePendingApprovals();
  const toPay = useOrdersToPay();
  const [done, setDone] = useState<Array<{ id: string; text: string }>>([]);
  const say = (id: string, text: string) => setDone((d) => [{ id, text }, ...d.filter((x) => x.id !== id)]);

  if (q.isPending) return <div className="h-24 animate-pulse rounded-lg bg-cream-300" aria-label="Loading" />;
  if (q.isError) return <p className="rounded-lg border border-rule bg-white p-6 text-sm text-rust-500">{apiMessage(q.error)}</p>;
  const payList = toPay.data ?? [];

  return (
    <div className="space-y-8">
      {done.length > 0 && (
        <div className="space-y-2">
          {done.map((d) => (
            <Notice key={d.id} tone="ok">
              {d.text}
            </Notice>
          ))}
        </div>
      )}
      <section className="space-y-4" aria-labelledby="approvals-waiting">
        <h2 id="approvals-waiting" className="font-display text-lg font-bold tracking-tight">
          Waiting for your approval
        </h2>
        {q.data.length === 0 ? (
          <p className="rounded-lg border border-rule bg-white p-6 text-sm text-brown-400">No orders waiting for your approval.</p>
        ) : (
          <ul className="space-y-4">
            {q.data.map((o) => (
              <ApprovalCard key={o.id} order={o} onDone={say} />
            ))}
          </ul>
        )}
      </section>
      {(payList.length > 0 || toPay.isError) && (
        <section className="space-y-4" aria-labelledby="approvals-to-pay">
          <div>
            <h2 id="approvals-to-pay" className="font-display text-lg font-bold tracking-tight">
              Approved — waiting for you to pay
            </h2>
            <p className="text-sm text-brown-400">These are paid with the company card. We start on each one as soon as it's paid.</p>
          </div>
          {toPay.isError ? (
            <p className="rounded-lg border border-rule bg-white p-6 text-sm text-rust-500">{apiMessage(toPay.error)}</p>
          ) : (
            <ul className="space-y-4">
              {payList.map((o) => (
                <ToPayCard key={o.id} order={o} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function ToPayCard({ order: o }: { order: StoreOrder }) {
  const payNow = usePayNow();
  const who = o.member?.name || o.member?.email || "Someone";
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">{who}</p>
          <p className="text-xs text-brown-400">
            Order {o.reference} · {formatDate(o.createdAt)}
            {o.approvedAt ? ` · approved ${formatDate(o.approvedAt)}` : ""}
          </p>
          <p className="mt-1 truncate text-sm text-brown-400">{itemsSummary(o.items)}</p>
        </div>
        <p className="shrink-0 text-right">
          <span className="block text-lg font-semibold tabular-nums text-ink">{money(o.total)}</span>
          <span className="text-xs text-brown-400">plus tax</span>
        </p>
      </div>
      {payNow.pay.isError && <p role="alert" className="text-sm text-rust-500">{apiMessage(payNow.pay.error)}</p>}
      <div className="flex flex-wrap items-start gap-2">
        {o.canPay && (
          <button type="button" className={PRIMARY} disabled={payNow.busy} onClick={() => payNow.start(o.id)}>
            {payNow.redirecting ? "Taking you to payment…" : "Pay now"}
          </button>
        )}
        <StoreCancelOrder order={o} />
      </div>
      <p className="text-xs">
        <Link to={`/store/orders?order=${encodeURIComponent(o.id)}`} className={LINK}>
          See the full order
        </Link>
      </p>
    </li>
  );
}

function ApprovalCard({ order: o, onDone }: { order: StoreOrder; onDone: (id: string, text: string) => void }) {
  const qc = useQueryClient();
  const [expected, setExpected] = useState<number | null>(o.total);
  const [priceMoved, setPriceMoved] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonMissing, setReasonMissing] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const busy = useRef(false);
  const who = o.member?.name || o.member?.email || "Someone";

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: STORE_PENDING_KEY });
    void qc.invalidateQueries({ queryKey: STORE_ADMIN_TO_PAY_KEY });
    void qc.invalidateQueries({ queryKey: STORE_ORDERS_KEY });
  };

  const approve = useMutation({
    mutationFn: () => approveStoreOrder(o.id, expected),
    onSuccess: (r: ApproveResult) => {
      if (r.payment?.url) {
        setRedirecting(true);
        refresh();
        window.location.assign(r.payment.url);
        return;
      }
      busy.current = false;
      const moved = r.priceChanged ? ` The price was re-checked: ${money(r.priceChanged.from)} → ${money(r.priceChanged.to)}.` : "";
      const next =
        r.placed || r.order.status === "placed"
          ? "It's placed and going into production."
          : r.order.status === "awaiting_payment" && r.order.paymentMethod === "employee_card"
            ? `We've emailed ${who} a link to pay.`
            : r.order.status === "awaiting_payment"
              ? "It's waiting for payment."
              : "We're putting it into production.";
      onDone(o.id, `Approved order ${o.reference} from ${who}. ${next}${moved}`);
      refresh();
    },
    onError: (err) => {
      busy.current = false;
      const n = newTotalFrom(err);
      if (n !== null) {
        setExpected(n);
        setPriceMoved(true);
        return;
      }
      // Approved, but the checkout didn't open (e.g. the card processor was
      // down): the order moves to "waiting for you to pay".
      refresh();
    },
  });

  const reject = useMutation({
    mutationFn: () => rejectStoreOrder(o.id, reason.trim()),
    onSuccess: () => {
      busy.current = false;
      onDone(o.id, `Declined order ${o.reference} from ${who}. We've emailed them your reason.`);
      refresh();
    },
    onError: () => {
      busy.current = false;
    },
  });

  const locked = approve.isPending || reject.isPending || redirecting;
  const moved = priceMoved && expected !== null;

  function onApprove() {
    if (busy.current || locked) return;
    busy.current = true;
    approve.mutate();
  }

  function onReject() {
    if (busy.current || locked) return;
    if (!reason.trim()) {
      setReasonMissing(true);
      return;
    }
    busy.current = true;
    reject.mutate();
  }

  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">{who}</p>
          {o.member?.email && o.member.name && <p className="truncate text-xs text-brown-400">{o.member.email}</p>}
          <p className="text-xs text-brown-400">
            Order {o.reference} · {formatDate(o.createdAt)}
            {o.paymentMethod ? ` · ${paymentMethodWords(o.paymentMethod)}` : ""}
          </p>
        </div>
        <p className="shrink-0 text-right">
          <span className="block text-lg font-semibold tabular-nums text-ink">{money(moved ? expected : o.total)}</span>
          {moved && <span className="text-xs text-brown-400 line-through">{money(o.total)}</span>}
        </p>
      </div>

      <ul className="space-y-0.5 text-sm text-ink">
        {o.items.map((it, i) => (
          <li key={`${it.productId}-${i}`} className="flex justify-between gap-3">
            <span className="min-w-0">
              {it.name} × {it.quantity}
              {it.colours.length ? <span className="text-brown-400"> · {it.colours.join(", ")}</span> : null}
            </span>
            <span className="shrink-0 tabular-nums text-brown-400">{money(it.total)}</span>
          </li>
        ))}
      </ul>
      {o.shipTo && (
        <p className="text-xs text-brown-400">
          Ship to {o.shipTo.name}, {o.shipTo.city}, {o.shipTo.stateCode}
        </p>
      )}

      {moved && <Notice tone="warn">The price is now {money(expected)}. Approve again to go ahead at the new price.</Notice>}
      {o.paymentMethod === "company_card" && !moved && (
        <p className="text-xs text-brown-400">This order is paid with the company card: approving takes you to pay for it.</p>
      )}
      {approve.isError && !newTotalFrom(approve.error) && <p role="alert" className="text-sm text-rust-500">{apiMessage(approve.error)}</p>}

      {rejecting ? (
        <div className="space-y-2">
          <label className="flex flex-col gap-1 text-xs text-brown-400">
            Why are you declining it? We'll email this to {who}.
            <textarea
              rows={3}
              value={reason}
              maxLength={2000}
              disabled={locked}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonMissing(false);
              }}
              className={INPUT}
            />
          </label>
          {reasonMissing && <p className="text-xs text-rust-500">Give a reason, so they know what to change.</p>}
          {reject.isError && <p role="alert" className="text-sm text-rust-500">{apiMessage(reject.error)}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" className={PRIMARY} disabled={locked} onClick={onReject}>
              {reject.isPending ? "Sending…" : "Decline and email reason"}
            </button>
            <button type="button" className={SECONDARY} disabled={locked} onClick={() => setRejecting(false)}>
              Back
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-2">
          <button type="button" className={PRIMARY} disabled={locked} onClick={onApprove}>
            {redirecting
              ? "Taking you to payment…"
              : approve.isPending
                ? "Approving…"
                : moved
                  ? `Approve at ${money(expected)}`
                  : o.paymentMethod === "company_card"
                    ? "Approve & pay"
                    : "Approve"}
          </button>
          <button type="button" className={SECONDARY} disabled={locked} onClick={() => setRejecting(true)}>
            Decline
          </button>
          <StoreCancelOrder order={o} />
        </div>
      )}
      <p className="text-xs">
        <Link to={`/store/orders?order=${encodeURIComponent(o.id)}`} className={LINK}>
          See the full order
        </Link>
      </p>
    </li>
  );
}
